/**
 * The cycle clock — every card's cycle as one ring of a dial.
 *
 * Each ring is a full cycle, however long that card's is, so they all share one
 * reading: **12 o'clock is the reset** (the close, when a new cycle opens) and a
 * ring's fill is how far round the card is today. A card about to reset is
 * nearly a full circle; one that just reset is a sliver. That shape answers
 * "where is each card in its cycle?" before a single date is read.
 *
 * Picking a ring (or a chip) brings that card forward: the centre counts down to
 * its reset, and the panel beside the dial walks its cycle — opened, today,
 * closes, statement, bill due — positioned by date. Projected dates say so
 * (§2.7); the one printed date, a bill still owed, is drawn solid and gold.
 */

import { useReducedMotion } from "framer-motion";
import { AlertTriangle, ArrowUpRight, Info, RotateCcw } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";

import { CardChips } from "@/components/cards/CardFan";
import {
  addDays,
  cardName,
  cycleProgress,
  daysBetween,
  PHASE_COPY,
  phaseOf,
} from "@/components/cycles/cycleMath";
import { Badge, Panel } from "@/components/ui/primitives";
import type { CycleCard } from "@/lib/api/client";
import { cn } from "@/lib/cn";
import { formatDate, formatMoney } from "@/lib/format";

const SIZE = 300;
const C = SIZE / 2;
const OUTER = 138;
const INNER_FREE = 70; // the centre's radius, kept clear for the countdown

export function CycleClock({
  cards,
  today,
  selectedId,
  onSelect,
}: {
  cards: CycleCard[];
  today: string;
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  const reduce = useReducedMotion();
  const [hoverId, setHoverId] = React.useState<string | null>(null);
  const [drawn, setDrawn] = React.useState(false);
  React.useEffect(() => {
    const t = setTimeout(() => setDrawn(true), 150);
    return () => clearTimeout(t);
  }, []);

  const n = cards.length;
  const pitch = Math.min(14, (OUTER - INNER_FREE) / Math.max(1, n));
  const stroke = Math.max(3.5, pitch * 0.62);
  const focus = cards.find((c) => c.card_id === (hoverId ?? selectedId)) ?? cards[0];
  const selected = cards.find((c) => c.card_id === selectedId) ?? cards[0];
  if (!focus || !selected) return null;

  return (
    <Panel className="animate-rise overflow-hidden" style={{ animationDelay: "60ms" }}>
      <div className="grid grid-cols-[minmax(0,1fr)] gap-0 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
        {/* --- the dial ---------------------------------------------------- */}
        <div className="flex flex-col items-center border-b border-hairline p-5 sm:p-6 lg:border-b-0 lg:border-r">
          <div className="flex w-full items-center justify-between">
            <span className="label-micro">Cycle clock</span>
            <span className="flex items-center gap-1 text-[11px] text-faint">
              <RotateCcw className="h-3 w-3" aria-hidden /> resets at the top
            </span>
          </div>

          <div className="relative mt-3 w-full max-w-[22rem]">
            <svg
              viewBox={`0 0 ${SIZE} ${SIZE}`}
              className="w-full"
              role="img"
              aria-label="Each ring is one card's billing cycle. The filled part is how far through the cycle it is today; the top is where it resets."
              onPointerLeave={() => setHoverId(null)}
            >
              {/* Reset tick at 12 o'clock. */}
              <line
                x1={C}
                y1={C - OUTER - 8}
                x2={C}
                y2={C - INNER_FREE + 6}
                className="stroke-hairline-strong"
                strokeWidth={1}
                strokeDasharray="2 3"
              />
              {cards.map((card, i) => {
                const r = OUTER - pitch * i - pitch / 2;
                const circ = 2 * Math.PI * r;
                const frac = cycleProgress(card);
                const isSel = card.card_id === selected.card_id;
                const isHover = card.card_id === hoverId;
                const lit = isSel || isHover;
                const colour = card.theme?.gradient_to ?? "rgb(var(--brass))";
                const angle = frac * 2 * Math.PI - Math.PI / 2;
                return (
                  <g
                    key={card.card_id}
                    className="cursor-pointer"
                    onPointerEnter={() => setHoverId(card.card_id)}
                    onClick={() => onSelect(card.card_id)}
                    style={{ opacity: lit || !hoverId ? 1 : 0.4, transition: "opacity 0.3s" }}
                  >
                    <title>{`${cardName(card)} •${card.last4} — day ${card.days_into_cycle} of ${card.cycle_length_days}`}</title>
                    {/* Hit area: the whole ring band, not just the thin stroke. */}
                    <circle cx={C} cy={C} r={r} fill="none" stroke="transparent" strokeWidth={pitch} />
                    <circle
                      cx={C}
                      cy={C}
                      r={r}
                      fill="none"
                      className="stroke-raised"
                      strokeWidth={stroke}
                    />
                    <circle
                      cx={C}
                      cy={C}
                      r={r}
                      fill="none"
                      stroke={colour}
                      strokeWidth={lit ? stroke + 1.6 : stroke}
                      strokeLinecap="round"
                      strokeDasharray={circ}
                      strokeDashoffset={drawn || reduce ? circ * (1 - frac) : circ}
                      transform={`rotate(-90 ${C} ${C})`}
                      style={{
                        transition: reduce
                          ? "none"
                          : `stroke-dashoffset 1.3s cubic-bezier(0.16,1,0.3,1) ${0.1 + i * 0.07}s, stroke-width 0.3s`,
                        filter: isSel ? `drop-shadow(0 0 4px ${colour})` : undefined,
                      }}
                    />
                    {/* Today: a bead at the end of the fill. */}
                    <circle
                      cx={C + r * Math.cos(angle)}
                      cy={C + r * Math.sin(angle)}
                      r={lit ? stroke * 0.62 + 1 : stroke * 0.5}
                      className="fill-ink"
                      style={{
                        opacity: drawn || reduce ? 1 : 0,
                        transition: `opacity 0.4s ease ${1 + i * 0.07}s`,
                      }}
                    />
                  </g>
                );
              })}
            </svg>

            {/* The centre: countdown to the focused card's reset. */}
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div key={focus.card_id} className="animate-rise text-center" style={{ animationDuration: "0.45s" }}>
                <div className="font-display text-[2.6rem] font-light leading-none tracking-[-0.04em] text-ink tabular-nums">
                  {focus.days_left_in_cycle}
                </div>
                <div className="mt-1 text-[11px] text-muted">
                  {focus.days_left_in_cycle === 0
                    ? "resets tonight"
                    : `day${focus.days_left_in_cycle === 1 ? "" : "s"} to reset`}
                </div>
                <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
                  day {focus.days_into_cycle}/{focus.cycle_length_days}
                </div>
              </div>
            </div>
          </div>

          <CardChips
            className="mt-4 w-full sm:justify-center"
            items={cards.map((c) => ({
              id: c.card_id,
              issuer: c.issuer,
              name: cardName(c),
              last4: c.last4,
              theme: c.theme,
            }))}
            selectedId={selected.card_id}
            onSelect={(id) => id && onSelect(id)}
            hoverId={hoverId}
            onHover={setHoverId}
            label="Choose a card's cycle"
          />
        </div>

        {/* --- the focused card --------------------------------------------- */}
        <FocusPanel card={selected} today={today} />
      </div>
    </Panel>
  );
}

function FocusPanel({ card, today }: { card: CycleCard; today: string }) {
  const phase = phaseOf(card);
  const resetOn = card.cycle_end ? addDays(card.cycle_end, 1) : null;

  return (
    <div key={card.card_id} className="animate-rise p-5 sm:p-6" style={{ animationDuration: "0.5s" }}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="label-micro truncate">{card.issuer}</div>
          <Link
            to={`/cards/${card.card_id}`}
            className="group mt-1 inline-flex max-w-full items-baseline gap-2 text-xl font-medium tracking-[-0.03em] text-ink"
          >
            <span className="truncate">{cardName(card)}</span>
            <span className="accent-serif text-lg text-muted">•{card.last4}</span>
            <ArrowUpRight className="h-4 w-4 self-center text-faint transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-ink" />
          </Link>
        </div>
        {phase && (
          <Badge
            tone={phase === "fresh" ? "paid" : phase === "mid" ? "neutral" : "warn"}
            className="shrink-0"
            title={PHASE_COPY[phase].hint}
          >
            {PHASE_COPY[phase].label}
          </Badge>
        )}
      </div>
      {phase && <p className="mt-2 text-[13px] text-muted">{PHASE_COPY[phase].hint}</p>}

      <Journey card={card} today={today} />

      <dl className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Stat
          label="Buy today, pay by"
          value={card.due_expected_on ? formatDate(card.due_expected_on).slice(0, 6) : "—"}
          note={card.float_days !== null ? `${card.float_days} days interest-free` : "no due rule yet"}
          projected
          accent
        />
        <Stat
          label="New cycle opens"
          value={resetOn ? formatDate(resetOn).slice(0, 6) : "—"}
          note={
            card.days_left_in_cycle === null
              ? undefined
              : card.days_left_in_cycle === 0
                ? "tomorrow"
                : `in ${card.days_left_in_cycle + 1} days`
          }
          projected
        />
        <Stat
          label="Cycle · grace"
          value={`${card.cycle_length_days ?? "—"}d · ${card.grace_days ?? "—"}d`}
          note="length · days to pay"
          projected
        />
      </dl>

      {card.outstanding !== null && card.outstanding_due_date !== null && (
        <p className="mt-4 rounded-lg border border-brass/25 bg-brass/[0.06] px-3 py-2 text-xs text-muted">
          <span className="font-medium text-brass">{formatMoney(card.outstanding)}</span> from the
          last statement {card.outstanding_due_date < today ? "was" : "is"} due{" "}
          {formatDate(card.outstanding_due_date)} — a date the issuer printed, not a projection.
          {card.outstanding_due_date < today &&
            " A payment only shows on the next statement, so this is awaiting confirmation, not unpaid."}
        </p>
      )}

      {/* A bill that exists and can't be seen is a problem; a cycle with
          nothing to bill is not. Only the first gets the amber treatment. */}
      {card.statements_missing > 0 ? (
        <p className="mt-3 flex items-start gap-2 rounded-lg border border-warn/25 bg-warn/[0.06] p-3 text-xs text-warn">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>
            {card.statements_missing} statement{card.statements_missing === 1 ? "" : "s"} missing
            since {formatDate(card.last_statement_date)} — this card had spending, or a balance
            owing, in {card.statements_missing === 1 ? "that cycle" : "those cycles"}, so a bill
            should have been issued. The cycle shown is projected from this card's usual dates.
          </span>
        </p>
      ) : (
        card.quiet_cycles > 0 && (
          <p className="mt-3 flex items-start gap-2 rounded-lg border border-hairline bg-raised/40 p-3 text-xs text-muted">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-faint" aria-hidden />
            <span>
              No statement since {formatDate(card.last_statement_date)}, and none was due:{" "}
              {card.quiet_cycles} cycle{card.quiet_cycles === 1 ? "" : "s"} passed with no
              spending and nothing owed, so the issuer had nothing to bill.
            </span>
          </p>
        )
      )}

      <p className="mt-4 text-[11px] text-faint" title={`${card.close_basis} ${card.due_basis}`}>
        {card.confidence === "observed"
          ? "Dates projected from this card's own statements, which all agree."
          : "Estimate — this card's history is short or uneven. Hover for the rule."}
      </p>
    </div>
  );
}

/**
 * One cycle, positioned by date: opened → today → closes → statement → due.
 * The rail is solid up to today (what has happened) and dashed beyond the close
 * (projection). Labels sit in a fixed row so bunched dates never collide.
 */
function Journey({ card, today }: { card: CycleCard; today: string }) {
  const reduce = useReducedMotion();
  const [drawn, setDrawn] = React.useState(false);
  React.useEffect(() => {
    setDrawn(false);
    const t = setTimeout(() => setDrawn(true), 80);
    return () => clearTimeout(t);
  }, [card.card_id]);

  if (!card.cycle_start || !card.cycle_end) return null;
  const start = card.cycle_start;
  const end = card.due_expected_on ?? card.cycle_end;
  const span = Math.max(1, daysBetween(start, end));
  const at = (iso: string) => Math.max(0, Math.min(100, (daysBetween(start, iso) / span) * 100));
  const todayAt = at(today);
  const closeAt = at(card.cycle_end);
  const owedAt =
    card.outstanding !== null && card.outstanding_due_date && card.outstanding_due_date >= today
      ? at(card.outstanding_due_date)
      : null;

  const marks: { at: number; label: string; date: string | null; tone: string }[] = [
    { at: 0, label: "Opened", date: start, tone: "text-muted" },
    { at: closeAt, label: "Closes", date: card.cycle_end, tone: "text-ink" },
    { at: 100, label: "Bill due", date: card.due_expected_on, tone: "text-muted" },
  ];

  return (
    <div className="mt-5" role="img" aria-label={`Cycle opened ${formatDate(start)}, closes ${formatDate(card.cycle_end)}, bill projected due ${formatDate(card.due_expected_on)}.`}>
      <div className="relative h-6">
        <div className="absolute inset-x-0 top-[0.6rem] h-1.5 rounded-full bg-raised" />
        <div
          className="absolute top-[0.85rem] border-t border-dashed border-hairline-strong"
          style={{ left: `${closeAt}%`, right: 0 }}
        />
        <div
          className="absolute left-0 top-[0.6rem] h-1.5 rounded-full"
          style={{
            width: drawn || reduce ? `${todayAt}%` : "0%",
            backgroundImage: card.theme
              ? `linear-gradient(90deg, ${card.theme.gradient_from}, ${card.theme.gradient_to})`
              : undefined,
            transition: reduce ? "none" : "width 1s cubic-bezier(0.16,1,0.3,1)",
          }}
        />
        {/* Today */}
        <span
          className="absolute top-0 h-6 w-0.5 -translate-x-1/2 rounded bg-ink"
          style={{ left: `${todayAt}%` }}
          title={`Today — day ${card.days_into_cycle} of ${card.cycle_length_days}`}
        />
        {/* Close: hollow, it hasn't happened. */}
        <span
          className="absolute top-[0.35rem] h-3.5 w-3.5 -translate-x-1/2 rounded-full border-2 border-ink/60 bg-panel"
          style={{ left: `${closeAt}%` }}
          title={`Cycle closes ${formatDate(card.cycle_end)}. ${card.close_basis}`}
        />
        {/* Projected due: dashed. */}
        <span
          className="absolute top-[0.35rem] h-3.5 w-3.5 -translate-x-1/2 rounded-full border-2 border-dashed border-ink/40 bg-panel"
          style={{ left: "100%" }}
          title={`Bill projected due ${formatDate(card.due_expected_on)}. ${card.due_basis}`}
        />
        {/* A printed bill still owed: solid gold. */}
        {owedAt !== null && (
          <span
            className="absolute top-[0.35rem] h-3.5 w-3.5 -translate-x-1/2 rounded-full border-2 border-brass bg-brass"
            style={{ left: `${owedAt}%` }}
            title={`${formatMoney(card.outstanding)} due ${formatDate(card.outstanding_due_date)} — printed on the last statement.`}
          />
        )}
      </div>
      <div className="mt-2 grid grid-cols-3 text-xs">
        {marks.map((m, i) => (
          <div key={m.label} className={cn(i === 1 && "text-center", i === 2 && "text-right")}>
            <div className="label-micro">{m.label}</div>
            <div className={cn("tabular-nums", m.tone)}>{m.date ? formatDate(m.date) : "—"}</div>
            {i === 1 && (
              <div className="text-micro text-faint">
                {card.days_left_in_cycle === 0 ? "today" : `${card.days_left_in_cycle}d left`}
              </div>
            )}
            {i === 2 && <div className="text-micro text-faint">projected</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  note,
  projected = false,
  accent = false,
}: {
  label: string;
  value: string;
  note?: string;
  projected?: boolean;
  accent?: boolean;
}) {
  return (
    <div className="min-w-0 rounded-xl border border-hairline bg-raised/50 p-3">
      <dt className="truncate text-[11.5px] text-faint">{label}</dt>
      <dd
        className={cn(
          "mt-1 truncate text-[17px] font-medium tracking-[-0.02em]",
          accent ? "text-ink" : "text-ink/90",
        )}
      >
        {projected && <span className="mr-0.5 text-faint">~</span>}
        {value}
      </dd>
      {note && <dd className="mt-0.5 truncate text-[11px] text-faint">{note}</dd>}
    </div>
  );
}
